import React, { useCallback } from 'react';
import { useTranslation } from '@lib/useTypedTranslation';
import styled from 'styled-components';

import { Card } from '../../../components/card/card';
import { Loading } from '../../../components/loading/loading';
import { PageHeader } from '../../../layout/body/pageHeader';
import { useWorldRequest } from '../../../lib/useWorldRequest';
import { RequestInitWithRetry } from '../../../lib/request';
import { getUserTimeZone } from '../../../lib/timeZone';
import { useAlignCharts } from '../../../lib/useAlignCharts';
import { getAccessPointSignalLevelTrends, getAccessPointUniqueDevicesByTime, getAccessPointRssiTrend, getAccessPointDroppedConnectionsTrend } from '../../../services/core/accessPoints';
import { CoreAccessPointSamples } from './charts/coreAccessPointSamples';
import { CoreAccessPointScore } from './charts/coreAccessPointScore';
import { CoreAccessPointUniqueDevices } from './charts/coreAccessPointUniqueDevices';
import { CoreAccessPointMeanRssi } from './charts/coreAccessPointMeanRssi';
import { CoreAccessPointDroppedConnections } from './charts/coreAccessPointDroppedConnections';
import { Theme } from 'containers/app/themes';
import { Link } from 'react-router-dom';
import { useWorldRoutes } from '../../../routes/parts/allWorldRoutes';
import './style.css';
import moment from 'moment';
import { getRelativeHourlyDeviceDateParams, getRelativeHourlyUtcDateParams } from '@lib/dateParams';
import { getTimeZoneOffset } from '../performance/charts/lib';

const LinkToAccessPoints = styled(Link)`
  padding-right: 0.5em;
  color: ${({ theme }: { theme: Theme }) => theme.links.color};
`;

const ApDetails = styled.div`
  display: inline-block;
  padding-left: 0.5em;
`;

interface IProps {
  id: string,
  bssid: string,
  ssid?: string
  frequency?: string
}

export interface DateRange {
  from: number,
  to: number
}

export const CoreAccessPointPerformance = (props: IProps) => {
  const { id, bssid, ssid, frequency } = props;
  const { t } = useTranslation('coreAccessPoints');
  const routes = useWorldRoutes();
  const timeZone = getUserTimeZone();
  const chartCount = 5;
  const { addChart, clearCharts } = useAlignCharts(chartCount);
  const timeZoneOffset = getTimeZoneOffset(timeZone);
  const dateRange: DateRange = getRelativeHourlyDeviceDateParams(moment.utc(), timeZoneOffset, 30);
  const dateRangeToQuery: DateRange = getRelativeHourlyUtcDateParams(moment.utc(), 30);

  const dataFetcher = useCallback(() => {
    clearCharts();
    const params = {
      id,
      timeZone,
      from: dateRangeToQuery.from,
      to: dateRangeToQuery.to
    };
    return (options: RequestInitWithRetry) => {
      return Promise.all([
        getAccessPointSignalLevelTrends({ ...params })(options),
        getAccessPointUniqueDevicesByTime({ ...params })(options),
        getAccessPointRssiTrend({ ...params })(options),
        getAccessPointDroppedConnectionsTrend({ bssid, ssid, timeZone, from: dateRangeToQuery.from, to: dateRangeToQuery.to })(options)
      ]);
    };
  }, [clearCharts, timeZone, id, bssid, ssid, dateRangeToQuery.from, dateRangeToQuery.to]);

  const {
    loading: dataLoading,
    data: [sampleTrends, uniqueDevicesTrends, rssiTrends, droppedConnectionsTrends]
  } = useWorldRequest(dataFetcher, { initialData: [[], [], [], []], initialLoading: true });

  const subtitle = `BSSID: ${bssid} / SSID: ${ssid || '-'} / Frequency: ${frequency || '-'}`;
  const subHeader = (
    <>
      <LinkToAccessPoints to={routes.core.accessPoints}>{t('ACCESS_POINTS_TITLE')}</LinkToAccessPoints>
      <i className="fas fa-chevron-right" />
      <ApDetails>{subtitle}</ApDetails>
    </>
  );
  return (
    <div className="core-access-point-performance-container">
      <Card className="core-access-point" noPadding>
        <PageHeader header={t('ACCESS_POINT_PERFORMANCE')} subHeader={subHeader} />
        <Loading isLoading={dataLoading} transparentOverlay={false}>
          <div className="core-accessPoint__chart__samples">
            <CoreAccessPointSamples data={sampleTrends} dateRange={dateRange} onMount={addChart} />
          </div>
          <div className="core-accessPoint__chart__score">
            <CoreAccessPointScore data={sampleTrends} dateRange={dateRange} onMount={addChart} />
          </div>
          <div className="core-accessPoint__chart__unique-devices">
            <CoreAccessPointUniqueDevices data={uniqueDevicesTrends} dateRange={dateRange} onMount={addChart} />
          </div>
          <div className="core-accessPoint__chart__mean-rssi">
            <CoreAccessPointMeanRssi data={rssiTrends} dateRange={dateRange} onMount={addChart} />
          </div>
          <div className="core-accessPoint__chart__dropped-connections">
            <CoreAccessPointDroppedConnections data={droppedConnectionsTrends} dateRange={dateRange} onMount={addChart} />
          </div>
        </Loading>
      </Card>
    </div>
  );
};
